// src/components/EscucharCancion.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Card, Spin, Typography, message } from 'antd';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';

const { Title } = Typography;

const LOGO_URL = 'https://cantalab.com/wp-content/uploads/2024/06/cropped-cantalab-logo-512px.png';
const PURCHASE_URL = 'https://cantalab.com/carrito-cantalab/';

const API_BASE =
  (import.meta?.env?.VITE_API_BASE) ||
  (process.env.REACT_APP_API_BASE) ||
  'https://crm-usa-cantalab.onrender.com';

export default function EscucharCancion() {
  const { phone: phoneParam } = useParams();
  const phone = String(phoneParam || '').replace(/\D/g, '');
  const db = getFirestore();

  const [loading, setLoading]   = useState(true);
  const [record, setRecord]     = useState(null);
  const [clipUrl, setClipUrl]   = useState('');
  const [err, setErr]           = useState('');

  const [isPlaying, setIsPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [maxPlays, setMaxPlays]   = useState(2);
  const [allowPlay, setAllowPlay] = useState(true);

  const [songTitle, setSongTitle] = useState('Tu canción');
  const [fired50, setFired50]     = useState(false);

  const audioRef = useRef(null);

  function extractTitleFromLyrics(lyrics) {
    const txt = String(lyrics || '').trim();
    if (!txt) return '';

    const md = txt.match(/^\s*\*\*(.+?)\*\*/m);
    if (md?.[1]) return md[1].trim();

    const html = txt.match(/<\s*(strong|b)\s*>([^<]+)<\/\s*\1\s*>/i);
    if (html?.[2]) return html[2].trim();

    const first = txt
      .split(/\r?\n+/)
      .map(s => s.trim())
      .find(s => s.length > 0);
    if (first && !/^(verso|coro|bridge)/i.test(first)) {
      return first.replace(/^t[ií]tulo[:\s-]+/i, '').trim();
    }
    return '';
  }

  function deriveTitle(rec) {
    if (rec?.title && String(rec.title).trim()) return String(rec.title).trim();
    const fromLyrics = extractTitleFromLyrics(rec?.lyrics);
    return fromLyrics || 'Tu canción';
  }

  async function fetchRecord() {
    setErr('');
    try {
      const ref1 = doc(db, 'musica', phone);
      const snap1 = await getDoc(ref1);
      let rec = null;

      if (snap1.exists()) {
        rec = { id: snap1.id, ...snap1.data() };
      } else {
        const q = query(collection(db, 'musica'), where('leadPhone', '==', phone));
        const qs = await getDocs(q);
        if (!qs.empty) {
          const items = qs.docs.map(d => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          rec = items[0];
        }
      }

      if (!rec) {
        setRecord(null);
        setClipUrl('');
        setSongTitle('Tu canción');
        setPlayCount(0);
        setMaxPlays(2);
        setAllowPlay(true);
        setFired50(false);
        return;
      }

      setRecord(rec);
      setClipUrl(rec.clipUrl || '');
      setPlayCount(Number(rec.playCount || 0));
      setMaxPlays(Number(rec.maxPlays || 2));
      setAllowPlay(rec.allowPlay !== false);
      setSongTitle(deriveTitle(rec));
      setFired50(!!rec.postListenFired);
    } catch (e) {
      console.error(e);
      setErr('No pude consultar la canción. Intenta más tarde.');
    }
  }

  useEffect(() => {
    let timer;
    (async () => {
      setLoading(true);
      await fetchRecord();
      setLoading(false);
      const started = Date.now();
      timer = setInterval(async () => {
        if (clipUrl || Date.now() - started > 5 * 60 * 1000) {
          clearInterval(timer);
          return;
        }
        await fetchRecord();
      }, 8000);
    })();
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const onEnded = async () => {
    setIsPlaying(false);
    const next = Math.min(playCount + 1, maxPlays);
    setPlayCount(next);
    try {
      if (record?.id) {
        await updateDoc(doc(db, 'musica', record.id), {
          playCount: next,
          lastPlayAt: new Date(),
          ...(next >= maxPlays ? { allowPlay: false } : {})
        });
      }
    } catch (e) {
      console.warn('No pude actualizar playCount:', e?.message);
    }
  };

  const onTimeUpdate = async () => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const percent = Math.round((el.currentTime / el.duration) * 100);

    if (!fired50 && percent >= 50) {
      setFired50(true);

      try {
        if (record?.id) {
          await updateDoc(doc(db, 'musica', record.id), {
            postListenFired: true,
            postListenAt: new Date(),
          });
        }
      } catch (e) {
        console.warn('No pude marcar postListenFired:', e?.message);
      }

      const leadId = `${phone}@s.whatsapp.net`;
      fetch(`${API_BASE}/api/sequences/enqueue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadId,
          trigger: 'LeadSeguimiento',
        }),
      }).catch(() => {});
    }
  };

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el) return;
    const exhausted = (!allowPlay || playCount >= maxPlays);
    if (exhausted) {
      message.error('Ya agotaste las reproducciones permitidas.');
      try { el.pause(); el.currentTime = 0; } catch {}
      return;
    }
    try {
      if (isPlaying) {
        await el.pause();
        setIsPlaying(false);
      } else {
        await el.play();
        setIsPlaying(true);
      }
    } catch {
      message.error('No se pudo reproducir el audio.');
    }
  };

  const goToPurchase = () => {
    window.location.href = PURCHASE_URL;
  };

  if (!phone) {
    return (
      <div style={{ maxWidth: 460, margin: '40px auto', padding: 16 }}>
        <Alert type="error" message="Teléfono inválido en la URL." />
      </div>
    );
  }

  const exhausted = (!allowPlay || playCount >= maxPlays);

  return (
    <div style={{ maxWidth: 460, margin: '40px auto', padding: '0 16px' }}>
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 12 }}>
        <img src={LOGO_URL} alt="Cantalab" width={220} height={66} style={{ borderRadius: 16 }} />
      </div>

      <Title level={4} style={{ textAlign: 'center', marginBottom: 18 }}>
        Escucha tu canción: “{songTitle}”
      </Title>

      <Spin spinning={loading}>
        {err && <Alert type="error" message={err} style={{ marginBottom: 16 }} />}
        {!loading && !clipUrl && (
          <Card style={{ textAlign: 'center' }}>Aún no encuentro tu clip</Card>
        )}

        {!loading && clipUrl && (
          <Card bodyStyle={{ padding: 24, position: 'relative' }}>
            <audio
              ref={audioRef}
              preload="auto"
              src={clipUrl}
              controls={false}
              onEnded={onEnded}
              onTimeUpdate={onTimeUpdate}
            />

            {/* Ondas solo animan si isPlaying */}
            <div style={styles.waveWrap}>
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    ...styles.bar,
                    background: '#010440',
                    animationDelay: `${i * 0.06}s`,
                    animationPlayState: isPlaying ? 'running' : 'paused'
                  }}
                />
              ))}
            </div>

            {/* Botón central: play/pausa o compra (reemplazo) */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>
              {exhausted ? (
                <button
                  onClick={goToPurchase}
                  style={{
                    ...styles.playButton,            // Reutilizamos tamaño/forma para ocupar el mismo lugar
                    background: '#22c55e',
                    boxShadow: '0 12px 24px rgba(34,197,94,.35)',
                    fontSize: 18,
                    fontWeight: 700,
                    padding: 0,
                    width: 300,
                    height: 60,
                  }}
                >
                 Quiero mi cancion completa  🎵
                </button>
              ) : (
                <button
                  onClick={togglePlay}
                  style={{
                    ...styles.playButton,
                    background: '#010440',
                    ...(isPlaying ? styles.playActive : {}),
                  }}
                >
                  {isPlaying ? '❚❚' : '▶'}
                </button>
              )}
            </div>
          </Card>
        )}
      </Spin>
    </div>
  );
}

const styles = {
  waveWrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(24, 1fr)',
    gap: 6,
    height: 120,
    alignItems: 'end',
  },
  bar: {
    width: '100%',
    height: 50,
    borderRadius: 4,
    animation: 'bounce 1.6s ease-in-out infinite',
  },
  playButton: {
    width: 160,             // un poco más ancho para que “Pagar canción” quepa
    height: 96,
    borderRadius: 14,
    border: 'none',
    cursor: 'pointer',
    color: '#fff',
    fontSize: 26,
    display: 'grid',
    placeItems: 'center',
    boxShadow: '0 12px 24px rgba(1,4,64,0.3)',
  },
  playActive: { transform: 'scale(0.96)' },
};

// keyframes para las barras
const styleTag = document.createElement('style');
styleTag.innerHTML = `
@keyframes bounce {
  0%, 100% { transform: scaleY(0.4) }
  50%      { transform: scaleY(1) }
}`;
if (typeof document !== 'undefined') document.head.appendChild(styleTag);
